"use client";

import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "./Masters/Button";
import RTE from "./RTE";
import FeatureInformation from "./FeatureInformation";
import ItineraryInformation from "./ItineraryInformation";
import PricePaymentInformation from "./PricePaymentInformation";
import PictureMetaInformation from "./PictureMetaInformation";
import TourInclusions from "./TourInclusions";
import TourAvailabilityOthers from "./TourAvailabilityOthers";
import { useTourBookingType, useGetCountries, useGetCompany, useGetCityByName, useGetTourTypeTheme, useGetTourInclusionDetails, useAddTourDetail, useGetTourCategories } from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";

interface AddDynamicPackageFormProps {
  onBack: () => void;
}

const TAB_OPTIONS = [
  "General Information",
  "Feature Information",
  "Itinerary Information",
  "Price & Payment Information",
  "Picture Meta Information",
  "Tour Inclusions",
  "Tour Availability & Others",
];

// Helper function to create optional number schema that handles empty strings
const createOptionalNumber = () => {
  return z.preprocess(
    (val) => {
      if (val === "" || val === null || val === undefined) return undefined;
      const num = Number(val);
      return isNaN(num) ? undefined : num;
    },
    z.number().optional()
  ) as z.ZodType<number | undefined>;
};

// Zod Schema for Tour Detail Form
const tourDetailSchema = z.object({
  tours_type: createOptionalNumber(),
  country_id: z.coerce.number().min(1, "Country is required"),
  city_id: createOptionalNumber(),
  post_code: z.string().optional(),
  tours_name: z.string().min(1, "Tour name is required"),
  tours_operator: z.coerce.number().min(1, "Tour operator is required"),
  starting_city: z.string().optional(),
  ending_city: z.string().optional(),
  destination_cover: z.string().optional(),
  no_of_traveller: z.string().min(1, "Number of traveller is required"),
  package_validity_from: z.string().optional(),
  package_validity_to: z.string().optional(),
  booking_validity_from: z.string().optional(),
  booking_validity_to: z.string().optional(),
  tours_code: z.string().optional(),
  rating: createOptionalNumber(),
  num_of_nights: createOptionalNumber(),
  num_of_days: createOptionalNumber(),
  tour_type: z.coerce.number().min(1).max(2, "Tour type must be 1 (Private) or 2 (Group)"),
  package_specification: z.string().optional(),
  short_desc: z.string().optional(),
  inclusions: createOptionalNumber(),
  theme: createOptionalNumber(),
  tour_category: z.string().optional(),
  departures: z.array(z.object({
    date: z.string(),
    time: z.string()
  })).optional(),
  // Feature Information fields
  tour_package_id: createOptionalNumber(),
  feature_start_city: z.string().optional(),
  feature_end_city: z.string().optional(),
  visiting_cities: z.string().optional(),
  accommodates: createOptionalNumber(),
  pickup_service: z.string().optional(),
  supplementary_room: z.string().optional(),
  location_type: z.string().optional(),
  scheduled_on: z.string().optional(),
  multilingual_guide: z.string().optional(),
  drop_off_service: z.string().optional(),
  entertainments: z.string().optional(),
  hotel_cities: z.array(z.object({
    city: z.string(),
    nights: z.string()
  })).optional(),
  lowest_cost_hotels: z.boolean().optional(),
  meal_plan_wise_hotels: z.boolean().optional(),
  // Picture Meta Information fields
  tours_image_primary: z.string().optional(),
  tours_image: z.string().optional(),
  spots_photo_gallery_primary: z.string().optional(),
  spots_photo_gallery: z.string().optional(),
  related_items: z.string().optional(),
  meta_title: z.string().optional(),
  meta_keywords: z.string().optional(),
  meta_desc: z.string().optional(),
}).refine(
  (data) => {
    if (data.tour_type === 2 && (!data.departures || data.departures.length === 0)) {
      return false;
    }
    return true;
  },
  {
    message: "At least one departure date and time is required for Group Tour",
    path: ["departures"],
  }
);

type TourDetailFormData = z.infer<typeof tourDetailSchema>;

const AddDynamicPackageForm: React.FC<AddDynamicPackageFormProps> = ({
  onBack,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const { user } = useAuth();
  const { mutate: addTourDetail, isPending: isSaving } = useAddTourDetail();
  const [selectedStartCities, setSelectedStartCities] = useState<any[]>([]);
  const [selectedEndCities, setSelectedEndCities] = useState<any[]>([]);
  const [selectedDestinationCities, setSelectedDestinationCities] = useState<any[]>([]);
  const [selectedDestinationCoverCities, setSelectedDestinationCoverCities] = useState<any[]>([]);
  // Feature Information city selections
  const [featureSelectedStartCities, setFeatureSelectedStartCities] = useState<any[]>([]);
  const [featureSelectedEndCities, setFeatureSelectedEndCities] = useState<any[]>([]);
  const [featureSelectedVisitingCities, setFeatureSelectedVisitingCities] = useState<any[]>([]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    formState: { errors },
  } = useForm<TourDetailFormData>({
    resolver: zodResolver(tourDetailSchema),
    defaultValues: {
      tour_type: 1, // Default to Private Tour
      departures: [{ date: "", time: "" }],
      package_specification: "Daily Itinerary Based", // Default package specification
      short_desc: "", // Default empty short description
    },
  });

  const onSubmit = (data: TourDetailFormData) => {
    console.log("=== FORM SUBMISSION STARTED ===");
    console.log("Form submitted with data:", data);
    console.log("User:", user);
    console.log("Selected start cities:", selectedStartCities);
    console.log("Selected end cities:", selectedEndCities);
    
    // Validate required fields
    if (!data.tours_name || !data.tours_name.trim()) {
      console.error("Tour name is required");
      alert("Please enter a tour name");
      return;
    }

    if (!data.country_id || data.country_id === 0) {
      console.error("Country is required");
      alert("Please select a destination country");
      return;
    }

    if (!data.no_of_traveller) {
      console.error("Number of traveller is required");
      alert("Please select number of travellers");
      return;
    }

    if (!data.tour_type) {
      console.error("Tour type is required");
      alert("Please select a tour type (Private or Group)");
      return;
    }

    if (!user?.company_id) {
      console.error("Missing company_id in user");
      alert("Company is not set for this user. Please ensure you are logged in with a company account.");
      return;
    }
    
    // Get city IDs from selected cities (comma-separated string like "1,2,3")
    const startingCityIds = selectedStartCities.map(c => c.city_id || c.id).filter(Boolean).join(",");
    const endingCityIds = selectedEndCities.map(c => c.city_id || c.id).filter(Boolean).join(",");
    const destinationCityIds = selectedDestinationCities.map(c => c.city_id || c.id).filter(Boolean).join(",");
    const destinationCoverIds = selectedDestinationCoverCities.map(c => c.city_id || c.id).filter(Boolean).join(",");
    
    // Handle inclusions - convert to comma-separated string if it's a number or array
    let inclusionIds = null;
    if (data.inclusions !== undefined && data.inclusions !== null) {
      if (Array.isArray(data.inclusions)) {
        inclusionIds = data.inclusions.join(",");
      } else {
        const inclusionValue = typeof data.inclusions === 'number' ? data.inclusions : Number(data.inclusions);
        if (!isNaN(inclusionValue) && inclusionValue > 0) {
          inclusionIds = String(inclusionValue);
        }
      }
    }
    
    // Handle theme - convert to comma-separated string if it's a number or array
    let themeIds = null;
    if (data.theme) {
      if (Array.isArray(data.theme)) {
        themeIds = data.theme.join(",");
      } else {
        themeIds = String(data.theme);
      }
    }
    
    // Handle tour category - get from form data or null
    const tourCategoryIds = data.tour_category || null;
    
    // Handle departure details for Group Tour (tour_type === 2)
    let departureDetails = null;
    if (data.tour_type === 2 && data.departures && data.departures.length > 0) {
      const validDepartures = data.departures.filter(d => d.date && d.time);
      if (validDepartures.length > 0) {
        departureDetails = {
          departure_date: validDepartures.map(d => d.date),
          departure_time: validDepartures.map(d => d.time)
        };
      }
    }

    // Map form data to API payload
    const payload = {
      company_id: user.company_id,
      tours_type: data.tours_type || null,
      country_id: data.country_id || 0,
      city_id: destinationCityIds ? destinationCityIds.split(",")[0] : (data.city_id || 0), // Use first selected destination city or form value
      post_code: data.post_code || null,
      tours_name: data.tours_name.trim(),
      tours_operator: data.tours_operator || null,
      starting_city: startingCityIds || null,
      ending_city: endingCityIds || null,
      destination_cover: destinationCoverIds || data.destination_cover || null,
      no_of_traveller: String(data.no_of_traveller),
      package_validity_from: data.package_validity_from || null,
      package_validity_to: data.package_validity_to || null,
      booking_validity_from: data.booking_validity_from || null,
      booking_validity_to: data.booking_validity_to || null,
      tours_code: data.tours_code || null,
      rating: data.rating || null,
      num_of_nights: data.num_of_nights || null,
      num_of_days: data.num_of_days || null,
      tour_type: data.tour_type,
      package_specification: data.package_specification || "Daily Itinerary Based",
      short_desc: data.short_desc || "",
      inclusion: inclusionIds, // Note: old code uses 'inclusion' not 'inclusions'
      theme: themeIds,
      tour_category: tourCategoryIds,
      departure_details: departureDetails,
      // Feature Information fields
      visiting_cities: featureSelectedVisitingCities.map(c => c.city_id || c.id).filter(Boolean).join(",") || data.visiting_cities || null,
      accommodates: data.accommodates || null,
      pickup_service: data.pickup_service || null,
      supplementary_room: data.supplementary_room || null,
      location_type: data.location_type || null,
      scheduled_on: data.scheduled_on || null,
      multilingual_guide: data.multilingual_guide || null,
      drop_off_service: data.drop_off_service || null,
      entertainments: data.entertainments || null,
      // Picture Meta Information fields
      tours_image_primary: data.tours_image_primary || null,
      tours_image: data.tours_image || null,
      spots_photo_gallery_primary: data.spots_photo_gallery_primary || null,
      spots_photo_gallery: data.spots_photo_gallery || null,
      related_items: data.related_items || null,
      meta_title: data.meta_title || null,
      meta_keywords: data.meta_keywords || null,
      meta_desc: data.meta_desc || null,
      user_id: user?.id || null,
      created_by: user?.id || null,
      ip: "127.0.0.1",
      status: 1,
    };

    console.log("Payload being sent:", JSON.stringify(payload, null, 2));
    console.log("Calling addTourDetail mutation...");

    try {
      addTourDetail(payload, {
        onSuccess: (response) => {
          console.log("=== SUCCESS RESPONSE ===");
          console.log("Success response:", response);
          console.log("Response status:", response?.status);
          console.log("Response data:", response?.data);
          
          if (response?.status === 'success') {
            console.log("Tour detail saved successfully!");
            reset();
            setSelectedStartCities([]);
            setSelectedEndCities([]);
            setSelectedDestinationCities([]);
            setSelectedDestinationCoverCities([]);
            setFeatureSelectedStartCities([]);
            setFeatureSelectedEndCities([]);
            setFeatureSelectedVisitingCities([]);
            onBack();
          } else {
            console.error("Unexpected response format:", response);
            alert(response?.message || "Failed to save tour detail. Please try again.");
          }
        },
        onError: (error) => {
          console.error("=== ERROR RESPONSE ===");
          console.error("Error saving tour detail:", error);
          console.error("Error object:", JSON.stringify(error, null, 2));
          console.error("Error response:", error?.response);
          console.error("Error response data:", error?.response?.data);
          console.error("Error message:", error?.message);
          const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || "Failed to save tour detail. Please check the console for details.";
          alert(errorMessage);
        },
      });
    } catch (error) {
      console.error("=== EXCEPTION IN ONSUBMIT ===");
      console.error("Exception:", error);
      alert("An unexpected error occurred. Please check the console.");
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return <GeneralInformation register={register} control={control} errors={errors} setSelectedStartCities={setSelectedStartCities} setSelectedEndCities={setSelectedEndCities} selectedStartCities={selectedStartCities} selectedEndCities={selectedEndCities} setSelectedDestinationCities={setSelectedDestinationCities} selectedDestinationCities={selectedDestinationCities} setSelectedDestinationCoverCities={setSelectedDestinationCoverCities} selectedDestinationCoverCities={selectedDestinationCoverCities} />;
      case 1:
        return <FeatureInformation 
          register={register} 
          control={control} 
          errors={errors} 
          watch={watch}
          setFeatureSelectedStartCities={setFeatureSelectedStartCities}
          setFeatureSelectedEndCities={setFeatureSelectedEndCities}
          setFeatureSelectedVisitingCities={setFeatureSelectedVisitingCities}
          featureSelectedStartCities={featureSelectedStartCities}
          featureSelectedEndCities={featureSelectedEndCities}
          featureSelectedVisitingCities={featureSelectedVisitingCities}
        />;
      case 2:
        return <ItineraryInformation />;
      case 3:
        return <PricePaymentInformation />;
      case 4:
        return <PictureMetaInformation register={register} control={control} errors={errors} watch={watch} />;
      case 5:
        return <TourInclusions />;
      case 6:
        return <TourAvailabilityOthers />;
      default:
        return (
          <div className="text-center py-8 text-gray-500 text-sm">
            {TAB_OPTIONS[activeTab]} - Coming Soon
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Top bar with title + actions */}
      <div className="border-b bg-white">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <h1 className="text-lg font-semibold text-gray-800">Add Package</h1>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={onBack}
            >
              Back
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => reset()}
            >
              Reset
            </Button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log("=== SAVE BUTTON CLICKED (RAW) ===");
                console.log("Form errors:", errors);
                console.log("Form values:", getValues());
                console.log("Is saving:", isSaving);
                console.log("User:", user);
                
                try {
                  handleSubmit(
                    (data) => {
                      console.log("=== VALIDATION PASSED ===");
                      console.log("Calling onSubmit with data:", data);
                      onSubmit(data);
                    },
                    (validationErrors) => {
                      console.error("=== VALIDATION FAILED ===");
                      console.error("Validation errors:", validationErrors);
                      // Show first error
                      const firstErrorKey = Object.keys(validationErrors)[0] as keyof typeof validationErrors;
                      if (firstErrorKey && validationErrors[firstErrorKey]) {
                        const errorMessage = (validationErrors[firstErrorKey] as any)?.message || `Please fix the error in ${firstErrorKey}`;
                        console.error("First error:", firstErrorKey, errorMessage);
                        alert(errorMessage);
                      } else {
                        console.error("Unknown validation error");
                        alert("Please fill in all required fields correctly");
                      }
                    }
                  )();
                } catch (error) {
                  console.error("=== EXCEPTION IN BUTTON CLICK ===", error);
                  alert("An error occurred. Check console for details.");
                }
              }}
              disabled={isSaving}
              className="px-3 py-1.5 text-sm bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 max-w-7xl mx-auto w-full px-4 py-4">
        <div className="flex gap-4">
          {/* Left side tabs */}
          <aside className="w-52 bg-white border border-gray-200 rounded-lg overflow-hidden text-xs">
            {TAB_OPTIONS.map((label, idx) => (
              <button
                key={label}
                type="button"
                onClick={() => setActiveTab(idx)}
                className={`w-full text-left px-3 py-2 border-b border-gray-100 hover:bg-blue-50 ${
                  activeTab === idx
                    ? "bg-blue-100 font-semibold"
                    : "bg-white"
                }`}
              >
                {label}
              </button>
            ))}
          </aside>

          {/* Right side form */}
          <form 
            onSubmit={(e) => {
              e.preventDefault();
              console.log("Form onSubmit event triggered");
              handleSubmit(onSubmit)(e);
            }} 
            className="flex-1 bg-white border border-gray-200 rounded-lg shadow-sm p-4 md:p-6 space-y-6"
          >
            {renderTabContent()}
            {/* Hidden submit button for form submission */}
            <button type="submit" style={{ display: 'none' }} aria-hidden="true" />
          </form>
        </div>
      </div>
    </div>
  );
};

// General Information Component
interface GeneralInformationProps {
  register: any;
  control: any;
  errors: any;
  setSelectedStartCities: React.Dispatch<React.SetStateAction<any[]>>;
  setSelectedEndCities: React.Dispatch<React.SetStateAction<any[]>>;
  selectedStartCities: any[];
  selectedEndCities: any[];
  setSelectedDestinationCities: React.Dispatch<React.SetStateAction<any[]>>;
  selectedDestinationCities: any[];
  setSelectedDestinationCoverCities: React.Dispatch<React.SetStateAction<any[]>>;
  selectedDestinationCoverCities: any[];
}

const GeneralInformation: React.FC<GeneralInformationProps> = ({ register, control, errors, setSelectedStartCities, setSelectedEndCities, selectedStartCities, selectedEndCities, setSelectedDestinationCities, selectedDestinationCities, setSelectedDestinationCoverCities, selectedDestinationCoverCities }) => {
  const { data: tourBookingTypeData, isLoading: isLoadingTourTypes } = useTourBookingType();
  
  // Extract tour types from API response
  // API returns: { success: true, data: [...] }
  const tourTypes = React.useMemo(() => {
    if (!tourBookingTypeData) return [];
    
    // Handle different response structures
    if (tourBookingTypeData.success && Array.isArray(tourBookingTypeData.data)) {
      return tourBookingTypeData.data;
    }
    if (Array.isArray(tourBookingTypeData.data)) {
      return tourBookingTypeData.data;
    }
    if (Array.isArray(tourBookingTypeData)) {
      return tourBookingTypeData;
    }
    return [];
  }, [tourBookingTypeData]);
  const { data: countries = [], isLoading: isLoadingCountries } = useGetCountries();
  const { data: companyListData, isLoading: isLoadingCompanies } = useGetCompany({});
  const { data: tourCategories = [], isLoading: isLoadingTourCategories } = useGetTourCategories();
  
  // Theme state
  const [selectedTourType, setSelectedTourType] = useState("");
  const [themes, setThemes] = useState<any[]>([]);
  const { mutate: fetchThemes, data: themesData, isPending: isLoadingThemes } = useGetTourTypeTheme();

  // Inclusion state
  const { data: inclusionData = [], isLoading: isLoadingInclusions } = useGetTourInclusionDetails();
  const inclusions = React.useMemo(() => {
    // useGetTourInclusionDetails returns res?.data?.responseData?.response
    // It might be an object: { status, data: [...] } or { data: [...] }
    console.log("Inclusion raw data:", inclusionData);

    if (!inclusionData) return [];

    // If already array
    if (Array.isArray(inclusionData)) return inclusionData;

    const anyData: any = inclusionData as any;

    // Common shapes: { status, data: [...] } or just { data: [...] }
    if (Array.isArray(anyData.data)) return anyData.data;
    if (Array.isArray(anyData.response?.data)) return anyData.response.data;

    // Fallback: try responseData.response.data
    if (Array.isArray(anyData.responseData?.response?.data)) {
      return anyData.responseData.response.data;
    }

    return [];
  }, [inclusionData]);

  // Tour mode & departures (for Group Tour)
  const [tourMode, setTourMode] = useState<"private" | "group">("private");
  const [departures, setDepartures] = useState<{ date: string; time: string }[]>([
    { date: "", time: "" },
  ]);
  
  // Fetch themes when component mounts or tour type changes
  React.useEffect(() => {
    fetchThemes({}, {
      onSuccess: (data) => {
        if (data?.status === "success" && Array.isArray(data?.data)) {
          setThemes(data.data);
        } else if (Array.isArray(data)) {
          setThemes(data);
        } else if (Array.isArray(data?.data)) {
          setThemes(data.data);
        }
      },
      onError: (error) => {
        console.error("Error fetching themes (non-blocking):", error);
        // Don't block form functionality if themes fail to load
        setThemes([]);
      }
    });
  }, [fetchThemes]);
  
  // City search and selection states
  const [startCitySearch, setStartCitySearch] = useState("");
  const [endCitySearch, setEndCitySearch] = useState("");
  const [destinationCitySearch, setDestinationCitySearch] = useState("");
  const [destinationCoverCitySearch, setDestinationCoverCitySearch] = useState("");
  const [showStartCityDropdown, setShowStartCityDropdown] = useState(false);
  const [showEndCityDropdown, setShowEndCityDropdown] = useState(false);
  const [showDestinationCityDropdown, setShowDestinationCityDropdown] = useState(false);
  const [showDestinationCoverCityDropdown, setShowDestinationCoverCityDropdown] = useState(false);
  
  // City search mutations
  const { mutate: searchStartCities, data: startCityResults, isPending: isSearchingStartCities } = useGetCityByName();
  const { mutate: searchEndCities, data: endCityResults, isPending: isSearchingEndCities } = useGetCityByName();
  const { mutate: searchDestinationCities, data: destinationCityResults, isPending: isSearchingDestinationCities } = useGetCityByName();
  const { mutate: searchDestinationCoverCities, data: destinationCoverCityResults, isPending: isSearchingDestinationCoverCities } = useGetCityByName();
  
  // Extract city arrays from response
  const startCitiesList = React.useMemo(() => {
    if (!startCityResults) return [];
    console.log('Start City Results:', startCityResults);
    console.log('Is Array?', Array.isArray(startCityResults));
    
    // The hook already extracts responseData.response.data, so it should be an array
    if (Array.isArray(startCityResults)) {
      console.log('Start Cities Count:', startCityResults.length);
      return startCityResults;
    }
    
    // Fallback: try to extract from nested structures
    if (Array.isArray(startCityResults?.data)) return startCityResults.data;
    if (Array.isArray(startCityResults?.cities)) return startCityResults.cities;
    if (Array.isArray(startCityResults?.responseData?.response?.data)) return startCityResults.responseData.response.data;
    if (Array.isArray(startCityResults?.responseData?.response)) return startCityResults.responseData.response;
    
    return [];
  }, [startCityResults]);
  
  const endCitiesList = React.useMemo(() => {
    if (!endCityResults) return [];
    console.log('End City Results:', endCityResults);
    console.log('Is Array?', Array.isArray(endCityResults));
    
    // The hook already extracts responseData.response.data, so it should be an array
    if (Array.isArray(endCityResults)) {
      console.log('End Cities Count:', endCityResults.length);
      return endCityResults;
    }
    
    // Fallback: try to extract from nested structures
    if (Array.isArray(endCityResults?.data)) return endCityResults.data;
    if (Array.isArray(endCityResults?.cities)) return endCityResults.cities;
    if (Array.isArray(endCityResults?.responseData?.response?.data)) return endCityResults.responseData.response.data;
    if (Array.isArray(endCityResults?.responseData?.response)) return endCityResults.responseData.response;
    
    return [];
  }, [endCityResults]);
  
  const destinationCitiesList = React.useMemo(() => {
    if (!destinationCityResults) return [];
    
    if (Array.isArray(destinationCityResults)) {
      return destinationCityResults;
    }
    
    // Fallback: try to extract from nested structures
    if (Array.isArray(destinationCityResults?.data)) return destinationCityResults.data;
    if (Array.isArray(destinationCityResults?.cities)) return destinationCityResults.cities;
    if (Array.isArray(destinationCityResults?.responseData?.response?.data)) return destinationCityResults.responseData.response.data;
    if (Array.isArray(destinationCityResults?.responseData?.response)) return destinationCityResults.responseData.response;
    
    return [];
  }, [destinationCityResults]);
  
  const destinationCoverCitiesList = React.useMemo(() => {
    if (!destinationCoverCityResults) return [];
    
    if (Array.isArray(destinationCoverCityResults)) {
      return destinationCoverCityResults;
    }
    
    // Fallback: try to extract from nested structures
    if (Array.isArray(destinationCoverCityResults?.data)) return destinationCoverCityResults.data;
    if (Array.isArray(destinationCoverCityResults?.cities)) return destinationCoverCityResults.cities;
    if (Array.isArray(destinationCoverCityResults?.responseData?.response?.data)) return destinationCoverCityResults.responseData.response.data;
    if (Array.isArray(destinationCoverCityResults?.responseData?.response)) return destinationCoverCityResults.responseData.response;
    
    return [];
  }, [destinationCoverCityResults]);
  
  // Search cities when user types
  React.useEffect(() => {
    if (startCitySearch.length >= 2) {
      searchStartCities({ cityname: startCitySearch });
      setShowStartCityDropdown(true);
    } else {
      setShowStartCityDropdown(false);
    }
  }, [startCitySearch, searchStartCities]);
  
  React.useEffect(() => {
    if (endCitySearch.length >= 2) {
      searchEndCities({ cityname: endCitySearch });
      setShowEndCityDropdown(true);
    } else {
      setShowEndCityDropdown(false);
    }
  }, [endCitySearch, searchEndCities]);
  
  React.useEffect(() => {
    if (destinationCitySearch.length >= 2) {
      searchDestinationCities({ cityname: destinationCitySearch });
      setShowDestinationCityDropdown(true);
    } else {
      setShowDestinationCityDropdown(false);
    }
  }, [destinationCitySearch, searchDestinationCities]);
  
  React.useEffect(() => {
    if (destinationCoverCitySearch.length >= 2) {
      searchDestinationCoverCities({ cityname: destinationCoverCitySearch });
      setShowDestinationCoverCityDropdown(true);
    } else {
      setShowDestinationCoverCityDropdown(false);
    }
  }, [destinationCoverCitySearch, searchDestinationCoverCities]);
  
  // Handle city selection
  const handleStartCitySelect = (city: any) => {
    const cityId = city.city_id || city.id;
    if (!selectedStartCities.find(c => (c.city_id || c.id) === cityId)) {
      setSelectedStartCities([...selectedStartCities, city]);
    }
    setStartCitySearch("");
    setShowStartCityDropdown(false);
  };
  
  const handleEndCitySelect = (city: any) => {
    const cityId = city.city_id || city.id;
    if (!selectedEndCities.find(c => (c.city_id || c.id) === cityId)) {
      setSelectedEndCities([...selectedEndCities, city]);
    }
    setEndCitySearch("");
    setShowEndCityDropdown(false);
  };
  
  // Remove selected city
  const removeStartCity = (cityId: number) => {
    setSelectedStartCities(selectedStartCities.filter(c => (c.city_id || c.id) !== cityId));
  };
  
  const removeEndCity = (cityId: number) => {
    setSelectedEndCities(selectedEndCities.filter(c => (c.city_id || c.id) !== cityId));
  };
  
  const handleDestinationCitySelect = (city: any) => {
    const cityId = city.city_id || city.id;
    if (!selectedDestinationCities.find(c => (c.city_id || c.id) === cityId)) {
      setSelectedDestinationCities([...selectedDestinationCities, city]);
    }
    setDestinationCitySearch("");
    setShowDestinationCityDropdown(false);
  };
  
  const removeDestinationCity = (cityId: number) => {
    setSelectedDestinationCities(selectedDestinationCities.filter(c => (c.city_id || c.id) !== cityId));
  };
  
  const handleDestinationCoverCitySelect = (city: any) => {
    const cityId = city.city_id || city.id;
    if (!selectedDestinationCoverCities.find(c => (c.city_id || c.id) === cityId)) {
      setSelectedDestinationCoverCities([...selectedDestinationCoverCities, city]);
    }
    setDestinationCoverCitySearch("");
    setShowDestinationCoverCityDropdown(false);
  };
  
  const removeDestinationCoverCity = (cityId: number) => {
    setSelectedDestinationCoverCities(selectedDestinationCoverCities.filter(c => (c.city_id || c.id) !== cityId));
  };
  
  // Close dropdowns when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.start-city-dropdown-container') && !target.closest('.start-city-input')) {
        setShowStartCityDropdown(false);
      }
      if (!target.closest('.end-city-dropdown-container') && !target.closest('.end-city-input')) {
        setShowEndCityDropdown(false);
      }
      if (!target.closest('.destination-city-dropdown-container') && !target.closest('.destination-city-input')) {
        setShowDestinationCityDropdown(false);
      }
      if (!target.closest('.destination-cover-city-dropdown-container') && !target.closest('.destination-cover-city-input')) {
        setShowDestinationCoverCityDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle company data structure (could be array or object)
  const companies = React.useMemo(() => {
    if (!companyListData) return [];
    const raw: any = companyListData;

    // Debug: log the raw data structure
    console.log('Company List Data:', raw);

    if (Array.isArray(raw)) {
      return raw;
    }

    // If it's an object, extract values
    if (typeof raw === 'object' && raw !== null) {
      const values = Object.values(raw).filter((item: any) => {
        if (!item || typeof item !== "object") return false;
        return (
          "company_name" in item ||
          "companyName" in item ||
          "name" in item ||
          "id" in item ||
          "company_id" in item
        );
      });
      return values;
    }

    return [];
  }, [companyListData]);

  return (
    <>
            {/* General Configuration */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-800">
                  General Configuration
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                {/* Left column */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-[11px] font-medium text-gray-700 mb-1">
                      Tour Type *
                    </label>
                    <select 
                      {...register("tours_type")}
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                      disabled={isLoadingTourTypes}
                      value={selectedTourType}
                      onChange={(e) => {
                        setSelectedTourType(e.target.value);
                        // Optionally filter themes by tour type
                        if (e.target.value) {
                          fetchThemes({ tour_type: e.target.value }, {
                            onSuccess: (data) => {
                              if (data?.status === "success" && Array.isArray(data?.data)) {
                                setThemes(data.data);
                              } else if (Array.isArray(data)) {
                                setThemes(data);
                              } else if (Array.isArray(data?.data)) {
                                setThemes(data.data);
                              }
                            }
                          });
                        } else {
                          // Fetch all themes if no tour type selected
                          fetchThemes({}, {
                            onSuccess: (data) => {
                              if (data?.status === "success" && Array.isArray(data?.data)) {
                                setThemes(data.data);
                              } else if (Array.isArray(data)) {
                                setThemes(data);
                              } else if (Array.isArray(data?.data)) {
                                setThemes(data.data);
                              }
                            }
                          });
                        }
                      }}
                    >
                      <option value="">-- Select Tour Type --</option>
                      {tourTypes.map((tourType: any) => (
                        <option key={tourType.id} value={tourType.id}>
                          {tourType.tour_type}
                        </option>
                      ))}
                    </select>
                    {errors.tours_type && (
                      <span className="text-red-500 text-[10px]">{errors.tours_type.message}</span>
                    )}
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-700 mb-1">
                      Destination Country *
                    </label>
                    <select 
                      {...register("country_id")}
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                      disabled={isLoadingCountries}
                    >
                      <option value="">-- Select Country --</option>
                      {Array.isArray(countries) && countries.map((country: any) => (
                        <option key={country.id} value={country.id}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                    {errors.country_id && (
                      <span className="text-red-500 text-[10px]">{errors.country_id.message}</span>
                    )}
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-700 mb-1">
                      Destination Post Code *
                    </label>
                    <input
                      {...register("post_code")}
                      type="text"
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-700 mb-1">
                      Tour Operator *
                    </label>
                    <select 
                      {...register("tours_operator")}
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                      disabled={isLoadingCompanies}
                    >
                      <option value="">-- Select Tour Operator --</option>
                      {Array.isArray(companies) && companies.length > 0 ? (
                        companies.map((company: any) => {
                          const companyId = company.id || company.company_id;
                          const companyName = company.company_name || company.companyName || company.name;
                          if (!companyId || !companyName) return null;
                          return (
                            <option key={companyId} value={companyId}>
                              {companyName}
                            </option>
                          );
                        })
                      ) : (
                        !isLoadingCompanies && <option disabled>No companies available</option>
                      )}
                    </select>
                    {errors.tours_operator && (
                      <span className="text-red-500 text-[10px]">{errors.tours_operator.message}</span>
                    )}
                  </div>
                </div>

                {/* Right column */}
                <div className="space-y-3">
                  <div className="relative destination-city-dropdown-container">
                    <label className="block text-[11px] font-medium text-gray-700 mb-1">
                      Destination City
                    </label>
                    {/* Combined textarea with selected cities */}
                    <div className="relative w-full border border-gray-300 rounded bg-white min-h-[60px]">
                      {/* Selected cities chips inside the textarea area */}
                      {selectedDestinationCities.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 p-2 pb-0">
                          {selectedDestinationCities.map((city) => {
                            const cityId = city.city_id || city.id;
                            const cityName = city.citystatecountry || city.city_name || city.name || 'City';
                            return (
                              <span
                                key={cityId}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-100 text-purple-800 rounded-md text-[10px] font-medium border border-purple-200"
                              >
                                {cityName}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeDestinationCity(cityId);
                                  }}
                                  className="hover:text-red-600 hover:bg-red-100 rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold"
                                  title="Remove city"
                                >
                                  ×
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {/* Search textarea */}
                      <textarea
                        value={destinationCitySearch}
                        onChange={(e) => setDestinationCitySearch(e.target.value)}
                        onFocus={() => destinationCitySearch.length >= 2 && setShowDestinationCityDropdown(true)}
                        placeholder={selectedDestinationCities.length > 0 ? "Type to search more cities..." : "Type to search cities..."}
                        rows={selectedDestinationCities.length > 0 ? 1 : 2}
                        className="destination-city-input w-full border-0 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-0 resize-none"
                        style={{ minHeight: selectedDestinationCities.length > 0 ? '32px' : '48px' }}
                      />
                    </div>
                    {/* Dropdown results */}
                    {showDestinationCityDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-y-auto">
                        {isSearchingDestinationCities ? (
                          <div className="px-3 py-2 text-xs text-gray-500">Searching...</div>
                        ) : destinationCitiesList.length > 0 ? (
                          destinationCitiesList
                            .filter((city: any) => {
                              const cityId = city.city_id || city.id;
                              return !selectedDestinationCities.find(c => (c.city_id || c.id) === cityId);
                            })
                            .map((city: any) => {
                              const cityId = city.city_id || city.id;
                              const cityName = city.citystatecountry || city.city_name || city.name || 'City';
                              return (
                                <div
                                  key={cityId}
                                  onClick={() => handleDestinationCitySelect(city)}
                                  className="px-3 py-2 text-xs hover:bg-purple-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                >
                                  {cityName}
                                </div>
                              );
                            })
                        ) : destinationCitySearch.length >= 2 ? (
                          <div className="px-3 py-2 text-xs text-gray-500">No cities found</div>
                        ) : null}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-700 mb-1">
                      Tour Name *
                    </label>
                    <input
                      {...register("tours_name")}
                      type="text"
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    {errors.tours_name && (
                      <span className="text-red-500 text-[10px]">{errors.tours_name.message}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Primary Information */}
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-gray-800">
                Primary Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                {/* Row 1: Start / End / Destination Covered */}
                <div className="relative start-city-dropdown-container">
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    Start City *
                  </label>
                  {/* Combined textarea with selected cities */}
                  <div className="relative w-full border border-gray-300 rounded bg-white min-h-[60px]">
                    {/* Selected cities chips inside the textarea area */}
                    {selectedStartCities.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 p-2 pb-0">
                        {selectedStartCities.map((city) => {
                          const cityId = city.city_id || city.id;
                          const cityName = city.citystatecountry || city.city_name || city.name || 'City';
                          return (
                            <span
                              key={cityId}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 text-blue-800 rounded-md text-[10px] font-medium border border-blue-200"
                            >
                              {cityName}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeStartCity(cityId);
                                }}
                                className="hover:text-red-600 hover:bg-red-100 rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold"
                                title="Remove city"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {/* Search textarea */}
                    <textarea
                      value={startCitySearch}
                      onChange={(e) => setStartCitySearch(e.target.value)}
                      onFocus={() => startCitySearch.length >= 2 && setShowStartCityDropdown(true)}
                      placeholder={selectedStartCities.length > 0 ? "Type to search more cities..." : "Type to search cities..."}
                      rows={selectedStartCities.length > 0 ? 1 : 2}
                      className="start-city-input w-full border-0 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-0 resize-none"
                      style={{ minHeight: selectedStartCities.length > 0 ? '32px' : '48px' }}
                    />
                  </div>
                  {/* Dropdown results */}
                  {showStartCityDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-y-auto">
                      {isSearchingStartCities ? (
                        <div className="px-3 py-2 text-xs text-gray-500">Searching...</div>
                      ) : startCitiesList.length > 0 ? (
                        startCitiesList
                          .filter((city: any) => {
                            const cityId = city.city_id || city.id;
                            return !selectedStartCities.find(c => (c.city_id || c.id) === cityId);
                          })
                          .map((city: any) => {
                            const cityId = city.city_id || city.id;
                            const cityName = city.citystatecountry || city.city_name || city.name || 'City';
                            return (
                              <div
                                key={cityId}
                                onClick={() => handleStartCitySelect(city)}
                                className="px-3 py-2 text-xs hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              >
                                {cityName}
                              </div>
                            );
                          })
                      ) : startCitySearch.length >= 2 ? (
                        <div className="px-3 py-2 text-xs text-gray-500">No cities found</div>
                      ) : null}
                    </div>
                  )}
                </div>
                <div className="relative end-city-dropdown-container">
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    End City *
                  </label>
                  {/* Combined textarea with selected cities */}
                  <div className="relative w-full border border-gray-300 rounded bg-white min-h-[60px]">
                    {/* Selected cities chips inside the textarea area */}
                    {selectedEndCities.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 p-2 pb-0">
                        {selectedEndCities.map((city) => {
                          const cityId = city.city_id || city.id;
                          const cityName = city.citystatecountry || city.city_name || city.name || 'City';
                          return (
                            <span
                              key={cityId}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-100 text-green-800 rounded-md text-[10px] font-medium border border-green-200"
                            >
                              {cityName}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeEndCity(cityId);
                                }}
                                className="hover:text-red-600 hover:bg-red-100 rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold"
                                title="Remove city"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {/* Search textarea */}
                    <textarea
                      value={endCitySearch}
                      onChange={(e) => setEndCitySearch(e.target.value)}
                      onFocus={() => endCitySearch.length >= 2 && setShowEndCityDropdown(true)}
                      placeholder={selectedEndCities.length > 0 ? "Type to search more cities..." : "Type to search cities..."}
                      rows={selectedEndCities.length > 0 ? 1 : 2}
                      className="end-city-input w-full border-0 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-0 resize-none"
                      style={{ minHeight: selectedEndCities.length > 0 ? '32px' : '48px' }}
                    />
                  </div>
                  {/* Dropdown results */}
                  {showEndCityDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-y-auto">
                      {isSearchingEndCities ? (
                        <div className="px-3 py-2 text-xs text-gray-500">Searching...</div>
                      ) : endCitiesList.length > 0 ? (
                        endCitiesList
                          .filter((city: any) => {
                            const cityId = city.city_id || city.id;
                            return !selectedEndCities.find(c => (c.city_id || c.id) === cityId);
                          })
                          .map((city: any) => {
                            const cityId = city.city_id || city.id;
                            const cityName = city.citystatecountry || city.city_name || city.name || 'City';
                            return (
                              <div
                                key={cityId}
                                onClick={() => handleEndCitySelect(city)}
                                className="px-3 py-2 text-xs hover:bg-green-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              >
                                {cityName}
                              </div>
                            );
                          })
                      ) : endCitySearch.length >= 2 ? (
                        <div className="px-3 py-2 text-xs text-gray-500">No cities found</div>
                      ) : null}
                    </div>
                  )}
                </div>
                <div className="relative destination-cover-city-dropdown-container">
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    Destination Covered *
                  </label>
                  {/* Combined textarea with selected cities */}
                  <div className="relative w-full border border-gray-300 rounded bg-white min-h-[60px]">
                    {/* Selected cities chips inside the textarea area */}
                    {selectedDestinationCoverCities.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 p-2 pb-0">
                        {selectedDestinationCoverCities.map((city) => {
                          const cityId = city.city_id || city.id;
                          const cityName = city.citystatecountry || city.city_name || city.name || 'City';
                          return (
                            <span
                              key={cityId}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-100 text-orange-800 rounded-md text-[10px] font-medium border border-orange-200"
                            >
                              {cityName}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeDestinationCoverCity(cityId);
                                }}
                                className="hover:text-red-600 hover:bg-red-100 rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold"
                                title="Remove city"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {/* Search textarea */}
                    <textarea
                      value={destinationCoverCitySearch}
                      onChange={(e) => setDestinationCoverCitySearch(e.target.value)}
                      onFocus={() => destinationCoverCitySearch.length >= 2 && setShowDestinationCoverCityDropdown(true)}
                      placeholder={selectedDestinationCoverCities.length > 0 ? "Type to search more cities..." : "Type to search cities..."}
                      rows={selectedDestinationCoverCities.length > 0 ? 1 : 2}
                      className="destination-cover-city-input w-full border-0 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-0 resize-none"
                      style={{ minHeight: selectedDestinationCoverCities.length > 0 ? '32px' : '48px' }}
                    />
                  </div>
                  {/* Dropdown results */}
                  {showDestinationCoverCityDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-y-auto">
                      {isSearchingDestinationCoverCities ? (
                        <div className="px-3 py-2 text-xs text-gray-500">Searching...</div>
                      ) : destinationCoverCitiesList.length > 0 ? (
                        destinationCoverCitiesList
                          .filter((city: any) => {
                            const cityId = city.city_id || city.id;
                            return !selectedDestinationCoverCities.find(c => (c.city_id || c.id) === cityId);
                          })
                          .map((city: any) => {
                            const cityId = city.city_id || city.id;
                            const cityName = city.citystatecountry || city.city_name || city.name || 'City';
                            return (
                              <div
                                key={cityId}
                                onClick={() => handleDestinationCoverCitySelect(city)}
                                className="px-3 py-2 text-xs hover:bg-orange-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              >
                                {cityName}
                              </div>
                            );
                          })
                      ) : destinationCoverCitySearch.length >= 2 ? (
                        <div className="px-3 py-2 text-xs text-gray-500">No cities found</div>
                      ) : null}
                    </div>
                  )}
                </div>

                {/* Row 2: Theme / Inclusion / Traveller */}
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    Theme *
                  </label>
                  <select 
                    {...register("theme")}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    disabled={isLoadingThemes}
                  >
                    <option value="">-- Select Theme --</option>
                    {Array.isArray(themes) && themes.map((theme: any) => (
                      <option key={theme.id || theme.auto_id} value={theme.id || theme.auto_id}>
                        {theme.theme_name || theme.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    Inclusion *
                  </label>
                  <select 
                    {...register("inclusions")}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    disabled={isLoadingInclusions}
                  >
                    <option value="">-- Select Inclusion --</option>
                    {Array.isArray(inclusions) && inclusions.map((incl: any) => (
                      <option key={incl.id} value={incl.id}>
                        {incl.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    Traveller *
                  </label>
                  <select {...register("no_of_traveller")} className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500">
                    <option value="">-- Select Traveler --</option>
                    {Array.from({ length: 7 }, (_, i) => i + 1).map((num) => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </select>
                  {errors.no_of_traveller && (
                    <span className="text-red-500 text-[10px]">{errors.no_of_traveller.message}</span>
                  )}
                </div>

              {/* Row 3: Package / Booking validity / Tour Code / Rating */}
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Package Validity *
                </label>
                <div className="flex gap-2">
                  <input
                    {...register("package_validity_from")}
                    type="date"
                    className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <input
                    {...register("package_validity_to")}
                    type="date"
                    className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Booking Validity *
                </label>
                <div className="flex gap-2">
                  <input
                    {...register("booking_validity_from")}
                    type="date"
                    className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <input
                    {...register("booking_validity_to")}
                    type="date"
                    className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Tour Code
                </label>
                <input
                  {...register("tours_code")}
                  type="text"
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Rating
                </label>
                <select {...register("rating")} className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500">
                  <option value="">-- Select Rating --</option>
                  {Array.from({ length: 5 }, (_, i) => i + 1).map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>
              </div>
            </div>

              {/* Nights / Days / Type / Category / Specification */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs pt-2 border-t border-gray-100 mt-2">
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    No. of Nights *
                  </label>
                  <input
                    {...register("num_of_nights")}
                    type="number"
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    No. of Days *
                  </label>
                  <input
                    {...register("num_of_days")}
                    type="number"
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    Tour Type *
                  </label>
                  <div className="flex items-center gap-4 mt-1">
                    <label className="inline-flex items-center gap-1 text-[11px]">
                      <input
                        type="radio"
                        {...register("tour_type")}
                        value={1}
                        checked={tourMode === "private"}
                        onChange={() => {
                          setTourMode("private");
                        }}
                      />
                      <span>Private Tour</span>
                    </label>
                    <label className="inline-flex items-center gap-1 text-[11px]">
                      <input
                        type="radio"
                        {...register("tour_type")}
                        value={2}
                        checked={tourMode === "group"}
                        onChange={() => {
                          setTourMode("group");
                        }}
                      />
                      <span>Group Tour</span>
                    </label>
                  </div>
                  {errors.tour_type && (
                    <span className="text-red-500 text-[10px]">{errors.tour_type.message}</span>
                  )}
                </div>
                {/* Departure (only for Group Tour) */}
              {tourMode === "group" && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    Departure *
                  </label>
                  <Controller
                    name="departures"
                    control={control}
                    render={({ field }) => (
                      <div className="space-y-2">
                        {departures.map((dep, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <input
                              type="date"
                              value={dep.date}
                              onChange={(e) => {
                                const next = [...departures];
                                next[index] = { ...next[index], date: e.target.value };
                                setDepartures(next);
                                field.onChange(next);
                              }}
                              className="w-40 border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            <input
                              type="time"
                              value={dep.time}
                              onChange={(e) => {
                                const next = [...departures];
                                next[index] = { ...next[index], time: e.target.value };
                                setDepartures(next);
                                field.onChange(next);
                              }}
                              className="w-32 border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            {departures.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const next = departures.filter((_, i) => i !== index);
                                  setDepartures(next);
                                  field.onChange(next);
                                }}
                                className="text-[11px] text-red-600 hover:text-red-700"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            const next = [...departures, { date: "", time: "" }];
                            setDepartures(next);
                            field.onChange(next);
                          }}
                          className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-[11px] rounded"
                        >
                          <span className="text-xs font-bold">+</span>
                          Add More
                        </button>
                      </div>
                    )}
                  />
                  {errors.departures && (
                    <span className="text-red-500 text-[10px]">{errors.departures.message}</span>
                  )}
                </div>
              )}

                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">

                  
                    Tour Categories *
                  </label>
                  <select
                    {...register("tour_category")}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    disabled={isLoadingTourCategories}
                  >
                    <option value="">-- Select Tour Category --</option>
                    {Array.isArray(tourCategories) &&
                      tourCategories.map((cat: any) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    Package Specification *
                  </label>
                  <div className="flex items-center gap-4 mt-1">
                    <label className="inline-flex items-center gap-1 text-[11px]">
                      <input 
                        type="radio" 
                        {...register("package_specification")}
                        value="Daily Itinerary Based"
                        defaultChecked
                      />
                      <span>Daily Itinerary Based</span>
                    </label>
                    <label className="inline-flex items-center gap-1 text-[11px]">
                      <input 
                        type="radio" 
                        {...register("package_specification")}
                        value="Only Hotel"
                      />
                      <span>Only Hotel</span>
                    </label>
                  </div>
                  {errors.package_specification && (
                    <span className="text-red-500 text-[10px]">{errors.package_specification.message}</span>
                  )}
                </div>
              </div>

              

              {/* Short Description (simple textarea instead of full RTE) */}
              <div className="pt-2 border-t border-gray-100 mt-2">
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Short Description
                </label>
                <Controller
                  name="short_desc"
                  control={control}
                  render={({ field }) => (
                    <RTE 
                      content={field.value || ""} 
                      onChange={(content) => field.onChange(content)}
                    />
                  )}
                />
                {errors.short_desc && (
                  <span className="text-red-500 text-[10px]">{errors.short_desc.message}</span>
                )}
              </div>
            </div>
    </>
  );
};

export default AddDynamicPackageForm;


